/**
 * api.ts — the single HTTP layer for CME Studio's two backends, with Auth0
 * token attachment. Every surface hook builds on this; nothing else should
 * call `fetch` directly.
 *
 * IMPORTANT — token audience per backend. The two backends each have their
 * OWN Auth0 API audience (see config.ts):
 *   - cmeinsight  → config.auth0Audience   (tender domain API, B1b)
 *   - wikiLLM     → config.wikiLLMAudience  (Knowledge Base service, B1a)
 * The `Auth0Provider` boots with only the tender audience, so a token for the
 * KB service must be minted on demand via
 * `getAccessTokenSilently({ authorizationParams: { audience } })`. `useApi()`
 * therefore exposes one client per backend, each pre-bound to the right
 * audience. (See wiki: Frontend_action_contract.md, B1b_backend_build_plan.md.)
 *
 * Every request is bounded by REQUEST_TIMEOUT_MS — EXCEPT file uploads, which
 * are exempt because their duration is the user's uplink, not the server's
 * health. `fetch` never times out on its own, and a promise that never settles
 * is invisible to every error path we have. See `resolveTimeout` below.
 */
import { useAuth0 } from "@auth0/auth0-react";
import { useMemo } from "react";

import { config } from "./config";

/** Thrown for every non-2xx response and for transport/token failures. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

/** Plain-Italian message for any thrown error, safe to show a non-technical user. */
/**
 * Turn FastAPI's `detail` into something a person can read.
 *
 * A hand-raised `HTTPException` puts a string there, but FastAPI's own
 * validation errors (422) put a LIST of `{loc, msg, type}` objects — and
 * `String(list)` renders that as "[object Object]", which is what the user saw
 * in the chat panel. Any 422 from any endpoint hit this.
 */
function describeDetail(detail: unknown): string {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((d) =>
        typeof d === "object" && d !== null && "msg" in d
          ? String((d as { msg: unknown }).msg)
          : typeof d === "string"
            ? d
            : "",
      )
      .filter(Boolean);
    if (messages.length) return messages.join(" · ");
  }
  return "Richiesta non valida.";
}

export function apiErrorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return "Errore imprevisto durante la chiamata al server.";
}

type QueryParams = Record<string, string | number | boolean | undefined>;

interface RequestOptions {
  signal?: AbortSignal;
  params?: QueryParams;
  /** JSON body — serialized and Content-Type set automatically. */
  body?: unknown;
  /** Multipart body — sent as-is so the browser sets the boundary. */
  formData?: FormData;
  /**
   * Deadline for THIS call, in ms. Omit for the default (REQUEST_TIMEOUT_MS, or
   * none when `formData` is present — see below); pass `null` to wait forever.
   */
  timeoutMs?: number | null;
  /**
   * Called with (bytesSent, bytesTotal) as the request body leaves the machine.
   *
   * Honoured only for `formData` requests, and passing it moves THAT call — and
   * only that call — onto `XMLHttpRequest`, because `fetch` exposes no
   * upload-progress event (`P-41`). Every request without this callback keeps
   * the fetch path untouched, so the blast radius of the second transport is
   * exactly the call sites that opted in.
   */
  onUploadProgress?: (loaded: number, total: number) => void;
  /**
   * Extra request headers, merged over the ones this layer builds.
   *
   * Added for `P-68`: the project assistant relays the signed-in user's
   * KNOWLEDGE BASE token to tender-api, which forwards it to the KB. Two
   * different APIs, two different audiences, so the second one cannot travel in
   * `Authorization` — that slot belongs to the backend being called.
   */
  headers?: Record<string, string>;
}

type TokenGetter = (audience: string) => Promise<string>;

/**
 * Hard ceiling on any single request. `fetch` has NO default timeout, so without
 * this a hung connection (dropped Wi-Fi, a proxy holding the socket open) leaves
 * the returned promise pending forever — not an error, just silence. Every
 * caller that releases state on settlement then hangs with it: TanStack mutations
 * stay `isPending`, and the Strategia row's optimistic lease
 * (StrategyTable.tsx) would freeze on un-saved values with no way back.
 * A timeout turns that into an ordinary rejection every existing error path
 * already handles. 30s is comfortably above the slowest normal call (AI quote
 * extraction and analyze runs are async jobs, not held-open requests).
 */
const REQUEST_TIMEOUT_MS = 30_000;

/** How long an Auth0 access token may take before we stop waiting. `R9` item 3.
 *
 * **The audit's finding: the 150 s assistant deadline is not the click-to-answer
 * budget.** That timer is created AFTER the token resolves, and before it there
 * are two token waits — the Knowledge Base pass minted in `askProjectAssistant`,
 * and the CME Studio token this client fetches — with no bound of our own around
 * either. A stalled provider keeps the question pending for as long as it likes,
 * and the literal in the comment beside `timeoutMs` would be describing part of
 * the request while reading as though it described all of it.
 *
 * Ten seconds because this is a CACHED read in the ordinary case — `auth0-spa-js`
 * returns a held token without a network call — so any wait approaching this is
 * already a silent refresh that has gone wrong. The total the browser can now
 * spend is stated rather than hoped for: at most
 * `2 × TOKEN_TIMEOUT_MS + timeoutMs`. */
const TOKEN_TIMEOUT_MS = 10_000;

/** `getToken` with a deadline, and an Italian sentence naming which pass failed.
 *
 * Rejects rather than resolving null: the caller decides what an absent token
 * means, and for the Knowledge Base pass that decision already exists — answer
 * from the Computo alone and say so. Swallowing it here would take that choice
 * away from the one place that knows it. */
async function withTokenDeadline(p: Promise<string>, which: string): Promise<string> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      p,
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(
          () =>
            reject(
              new ApiError(
                0,
                `Autenticazione ${which} non completata entro ${Math.round(
                  TOKEN_TIMEOUT_MS / 1000,
                )} secondi. Ricarica la pagina e riprova.`,
                null,
              ),
            ),
          TOKEN_TIMEOUT_MS,
        );
      }),
    ]);
  } finally {
    // Cleared on BOTH paths: a pending timer holds a reference to the rejection
    // and, in a test environment with fake timers, keeps the process alive.
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * How long `request` waits before aborting. A multipart body gets NO deadline.
 *
 * An upload's duration is set by the file size and the user's uplink, not by the
 * server's responsiveness: the backend accepts up to 1 GB
 * (tender-api/src/tender_api/api/documents.py), and a 50 MB drawings PDF over an
 * ordinary Italian FTTC uplink (1–20 Mbit/s up) already exceeds 30s. Any finite
 * ceiling would truncate somebody's real file, non-deterministically (fine on
 * office fibre, broken at home), and blame the server for a client-side abort.
 * `P-41` added an XHR transport that DOES emit upload progress for callers that
 * ask (see `onUploadProgress`) — that changes what the screen can show, not
 * this deadline reasoning: progress makes a slow upload visible, and we still
 * never abort one on the user's behalf.
 *
 * Keyed off the request SHAPE rather than opted out at each call site on
 * purpose: a new upload added later is exempt by construction instead of
 * silently inheriting a 30s cap. An explicit `timeoutMs` still overrides this.
 */
function resolveTimeout(options: RequestOptions): number | null {
  if (options.timeoutMs !== undefined) return options.timeoutMs;
  return options.formData ? null : REQUEST_TIMEOUT_MS;
}

/**
 * The upload transport, used only when a caller asks to observe progress.
 *
 * It reproduces `request`'s contract to the letter — same headers, same timeout
 * semantics (a multipart body defaults to NO deadline), same abort relay, and
 * the same four-way error taxonomy: status-carrying `ApiError` for a non-2xx,
 * `0` for transport failure, `408` for a deadline hit, `0 "illeggibile"` for a
 * 2xx whose body does not parse. A test that swaps one transport for the other
 * must not be able to tell them apart from the outside; the ONLY observable
 * difference is that this one emits progress events.
 *
 * No `Content-Type` is set for the FormData here either — XHR, like fetch,
 * writes the multipart boundary itself, and setting the header by hand would
 * produce a boundary-less body the server rejects.
 */
function xhrRequest<T>(
  url: string,
  method: string,
  headers: Record<string, string>,
  formData: FormData,
  timeoutMs: number | null,
  signal: AbortSignal | undefined,
  onUploadProgress: (loaded: number, total: number) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);

    let timedOut = false;
    const timer =
      timeoutMs === null
        ? null
        : setTimeout(() => {
            timedOut = true;
            xhr.abort();
          }, timeoutMs);
    const relayAbort = (): void => xhr.abort();
    signal?.addEventListener("abort", relayAbort);
    const cleanup = (): void => {
      if (timer !== null) clearTimeout(timer);
      signal?.removeEventListener("abort", relayAbort);
    };

    xhr.upload.onprogress = (e: ProgressEvent): void => {
      // Without a computable length there is no honest percentage — emit
      // nothing rather than a number invented from partial information.
      if (e.lengthComputable) onUploadProgress(e.loaded, e.total);
    };

    xhr.onload = (): void => {
      cleanup();
      const status = xhr.status;
      let body: unknown = null;
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        body = null;
      }
      if (status >= 200 && status < 300) {
        if (status === 204) {
          resolve(undefined as T);
          return;
        }
        if (body === null && xhr.responseText !== "null") {
          reject(new ApiError(0, "Risposta del server illeggibile.", null));
          return;
        }
        resolve(body as T);
        return;
      }
      const detail =
        typeof body === "object" && body !== null && "detail" in body
          ? describeDetail((body as { detail: unknown }).detail)
          : xhr.statusText || `Errore HTTP ${status}`;
      reject(new ApiError(status, detail, body));
    };
    xhr.onerror = (): void => {
      cleanup();
      reject(
        new ApiError(
          0,
          "Backend non raggiungibile. Verifica che il servizio sia in esecuzione.",
          null,
        ),
      );
    };
    xhr.ontimeout = (): void => {
      // Unreachable with our own setTimeout driving the abort, but XHR has a
      // native timeout event and leaving it unhandled would settle nothing.
      cleanup();
      reject(
        new ApiError(
          408,
          `Il server non ha risposto entro ${(timeoutMs ?? 0) / 1000} secondi. Riprova.`,
          null,
        ),
      );
    };
    xhr.onabort = (): void => {
      cleanup();
      reject(
        timedOut
          ? new ApiError(
              408,
              `Il server non ha risposto entro ${(timeoutMs ?? 0) / 1000} secondi. Riprova.`,
              null,
            )
          : new ApiError(0, "Richiesta annullata.", null),
      );
    };

    xhr.send(formData);
  });
}

function buildUrl(baseUrl: string, path: string, params?: QueryParams): string {
  const url = new URL(`${baseUrl.replace(/\/+$/, "")}${path}`);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

async function request<T>(
  baseUrl: string,
  audience: string,
  getToken: TokenGetter,
  method: string,
  path: string,
  options: RequestOptions,
): Promise<T> {
  // An ALREADY-aborted signal must not reach the network. Both transports only
  // ever *relayed* an abort that arrives later, so a caller that cancelled
  // before this call still minted a token and still sent the request — and on
  // the XHR path that means a whole multipart body leaves the machine after
  // the user navigated away. Checked before the token await so a cancelled
  // call costs nothing at all.
  if (options.signal?.aborted) {
    throw new ApiError(0, "Richiesta annullata.", null);
  }

  let token: string;
  try {
    // `R9` item 3. The fetch deadline below cannot start until this resolves, so
    // an unbounded wait here is an unbounded wait for the WHOLE request — the
    // 150 s literal would be a claim about part of it. Bounded here rather than
    // by widening that timer, because the two failures are different and deserve
    // different sentences: «the server took too long» and «we could not get a
    // pass to talk to it» are not the same news.
    token = await withTokenDeadline(getToken(audience), "CME Studio");
  } catch (cause) {
    // **`B-6`.** Our own error passes through UNCHANGED. Without this the
    // wrapper below re-labelled the token DEADLINE as `401` with «Effettua di
    // nuovo l'accesso» appended — so a slow silent renewal was presented as an
    // authorisation problem, three screens rendered their consent panel on it,
    // and the sentence contradicted itself mid-way. `getAccessTokenSilently`
    // rejects with auth0-spa-js error objects carrying `.error`, never with an
    // `ApiError`, so this fires only on the one we threw ourselves.
    if (cause instanceof ApiError) throw cause;
    // auth0-spa-js errors carry `.error` (e.g. login_required, consent_required,
    // invalid_audience) + `.error_description`. Surface them so token failures
    // are diagnosable instead of hidden behind a generic message.
    const code =
      cause && typeof cause === "object" && "error" in cause
        ? String((cause as { error: unknown }).error)
        : undefined;
    const desc =
      cause && typeof cause === "object" && "error_description" in cause
        ? String((cause as { error_description: unknown }).error_description)
        : cause instanceof Error
          ? cause.message
          : undefined;
    throw new ApiError(
      401,
      `Token non disponibile per il backend${code ? ` [${code}]` : ""}${
        desc ? `: ${desc}` : ""
      }. Effettua di nuovo l'accesso.`,
      cause,
    );
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...(options.headers ?? {}),
  };
  let bodyInit: BodyInit | undefined;
  if (options.formData) {
    bodyInit = options.formData;
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    bodyInit = JSON.stringify(options.body);
  }

  // A caller that wants to watch its upload leave the machine takes the XHR
  // transport; everything else stays on fetch. See `xhrRequest` for why.
  if (options.formData && options.onUploadProgress) {
    return xhrRequest<T>(
      buildUrl(baseUrl, path, options.params),
      method,
      headers,
      options.formData,
      resolveTimeout(options),
      options.signal,
      options.onUploadProgress,
    );
  }

  // Our own controller so the deadline can fire regardless of whether the caller
  // supplied a signal (TanStack passes one for queries, never for mutations).
  // The caller's signal is relayed into it so cancellation still works.
  const controller = new AbortController();
  const timeoutMs = resolveTimeout(options);
  let timedOut = false;
  const timer =
    timeoutMs === null
      ? null
      : setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, timeoutMs);
  const relayAbort = (): void => controller.abort();
  options.signal?.addEventListener("abort", relayAbort);

  /**
   * The deadline can fire during the fetch OR while the body is being streamed,
   * and both must surface as the same classified, Italian-language error — a
   * bare AbortError has no `.status` and is not an `instanceof Error` in every
   * runtime, so `apiErrorMessage` would degrade it to "Errore imprevisto".
   */
  const deadlineError = (cause: unknown): ApiError =>
    new ApiError(
      408,
      `Il server non ha risposto entro ${(timeoutMs ?? 0) / 1000} secondi. Riprova.`,
      cause,
    );

  try {
    let res: Response;
    try {
      res = await fetch(buildUrl(baseUrl, path, options.params), {
        method,
        headers,
        body: bodyInit,
        signal: controller.signal,
      });
    } catch (cause) {
      // A deadline hit is not the same story as an unreachable backend: the
      // service answered the TCP connect and then went quiet.
      if (timedOut) throw deadlineError(cause);
      // Transport-level failure (backend down, CORS, DNS) — distinct from an HTTP error.
      throw new ApiError(
        0,
        "Backend non raggiungibile. Verifica che il servizio sia in esecuzione.",
        cause,
      );
    }

    if (!res.ok) {
      const body = await safeJson(res); // swallows its own parse/abort failure
      if (timedOut) throw deadlineError(null);
      const detail =
        typeof body === "object" && body !== null && "detail" in body
          ? describeDetail((body as { detail: unknown }).detail)
          : res.statusText || `Errore HTTP ${res.status}`;
      throw new ApiError(res.status, detail, body);
    }

    if (res.status === 204) return undefined as T;
    try {
      return (await res.json()) as T;
    } catch (cause) {
      // Headers arrived, then the body stalled (deadline) or was malformed.
      // Classified here rather than left to propagate raw, so every caller sees
      // an ApiError whatever stage the request died at.
      if (timedOut) throw deadlineError(cause);
      throw new ApiError(0, "Risposta del server illeggibile.", cause);
    }
  } finally {
    // Cleared only once the BODY has been read too — aborting the controller
    // also aborts the response stream, so a server that sends headers and then
    // stalls mid-body hits the same deadline and the same 408 above.
    if (timer !== null) clearTimeout(timer);
    options.signal?.removeEventListener("abort", relayAbort);
  }
}

export interface BackendClient {
  get: <T>(path: string, options?: RequestOptions) => Promise<T>;
  post: <T>(path: string, options?: RequestOptions) => Promise<T>;
  put: <T>(path: string, options?: RequestOptions) => Promise<T>;
  patch: <T>(path: string, options?: RequestOptions) => Promise<T>;
  del: <T>(path: string, options?: RequestOptions) => Promise<T>;
}

function makeClient(baseUrl: string, audience: string, getToken: TokenGetter): BackendClient {
  return {
    get: <T>(path: string, options: RequestOptions = {}): Promise<T> =>
      request<T>(baseUrl, audience, getToken, "GET", path, options),
    post: <T>(path: string, options: RequestOptions = {}): Promise<T> =>
      request<T>(baseUrl, audience, getToken, "POST", path, options),
    put: <T>(path: string, options: RequestOptions = {}): Promise<T> =>
      request<T>(baseUrl, audience, getToken, "PUT", path, options),
    patch: <T>(path: string, options: RequestOptions = {}): Promise<T> =>
      request<T>(baseUrl, audience, getToken, "PATCH", path, options),
    del: <T>(path: string, options: RequestOptions = {}): Promise<T> =>
      request<T>(baseUrl, audience, getToken, "DELETE", path, options),
  };
}

export interface Api {
  /** Knowledge Base service (wiki-llm-service, B1a — live). */
  wikiLLM: BackendClient;
  /** Tender domain API (CMEInsight backend, B1b — not built yet). */
  cmeinsight: BackendClient;
  /**
   * A Knowledge Base access token for the signed-in user, or `null` if one
   * cannot be minted without interrupting them.
   *
   * `P-68`: «Assistente CME» answers from the tender's Computo AND the
   * customer's Knowledge Base, and the join happens in tender-api (`D-32`). So
   * tender-api needs to read the KB **as this user** — it relays this token
   * rather than holding a machine credential that could read every customer's
   * knowledge base.
   *
   * **`null` is a normal answer, not an error.** A user who has never opened
   * «Esplora KB» may owe Auth0 a one-time consent for that audience, and
   * `getAccessTokenSilently` rejects with `consent_required` /
   * `login_required` rather than silently succeeding. Throwing here would turn
   * "the assistant cannot see documents" into "the whole question failed",
   * which is exactly the wrong trade: the tender's own numbers are still
   * answerable, and the reply says plainly that the documents were not
   * available. Interrupting a question with a login popup is worse than
   * answering it with a disclosure.
   */
  kbToken: () => Promise<string | null>;
}

/**
 * Returns one HTTP client per backend, each pre-bound to the correct Auth0
 * audience and the current user's token getter. Stable across renders.
 */
export function useApi(): Api {
  const { getAccessTokenSilently } = useAuth0();
  return useMemo(() => {
    const getToken: TokenGetter = (audience) =>
      getAccessTokenSilently({ authorizationParams: { audience } });
    return {
      wikiLLM: makeClient(config.wikiLLMApiUrl, config.wikiLLMAudience, getToken),
      cmeinsight: makeClient(config.cmeinsightApiUrl, config.auth0Audience, getToken),
      kbToken: async () => {
        try {
          return await withTokenDeadline(getToken(config.wikiLLMAudience), "Knowledge Base");
        } catch {
          // Every failure is the same fact to the caller: no knowledge access
          // for this question. See the interface docstring for why this is not
          // rethrown.
          return null;
        }
      },
    };
  }, [getAccessTokenSilently]);
}
